// src/pages/public/CityLandingPage.tsx
import { useMemo } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import PublicSeoPage from './PublicSeoPage';
import { useSeoInventory, usePublicDirectory } from './usePublicSeoData';
import { citiesInState, cityRow, resolveCityDisplayName } from './publicPageHelpers';
import { getSeoContext } from '../../seo/seoContext';
import { buildCityModel } from '../../seo/core/pageModels';
import { resolveState } from '../../seo/core/states';
import { citySlug } from '../../seo/core/slug';
import { analyzeFacetedUrl, applyFacetsToMetadata } from '../../seo/core/facets';
import { ROUTES } from '../../seo/core/urls';
import type { PublicPageModel } from '../../seo/types';

/**
 * `/locations/:stateSlug/:citySlug` — city hub for any US city.
 *
 * Nothing about this page is hardcoded to a particular market: the city segment
 * is a slug of whatever city name real inventory carries, and the state segment
 * resolves against the full 50-states + DC registry.
 */
function CityLandingPage() {
  const ctx = getSeoContext();
  const params = useParams<{ stateSlug: string; citySlug: string }>();
  const [searchParams] = useSearchParams();
  const state = resolveState(params.stateSlug);
  const slug = citySlug(params.citySlug);
  const { inventory, loading: inventoryLoading } = useSeoInventory();

  const cityName = state ? resolveCityDisplayName(inventory, state.code, slug) : '';

  const { directory, loading: directoryLoading } = usePublicDirectory(
    { state: state?.code, city: cityName, limit: 24 },
    Boolean(state && cityName)
  );

  const model = useMemo<PublicPageModel | null>(() => {
    if (!state || !cityName) return null;
    const row = cityRow(inventory, state.code, slug);

    const built = buildCityModel(
      {
        city: cityName,
        state: state.code,
        stylistCount: row?.stylistCount ?? directory.total,
        priceMin: directory.priceMin,
        priceMax: directory.priceMax,
        stylists: directory.stylists,
        services: row?.services || [],
        nearbyCities: citiesInState(inventory, state.code)
          .filter((other) => citySlug(other.city) !== slug)
          .sort((a, b) => b.stylistCount - a.stylistCount)
          .slice(0, 12),
      },
      ctx
    ) as PublicPageModel | null;
    if (!built) return null;

    const analysis = analyzeFacetedUrl(ROUTES.city(state.slug, cityName), searchParams, {
      baseIndexable: built.metadata.indexable,
    });
    return { ...built, metadata: applyFacetsToMetadata(built.metadata, analysis, ctx) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, cityName, slug, inventory, directory, searchParams.toString(), ctx.environment, ctx.siteUrl]);

  if (!state) return <Navigate to={ROUTES.locationIndex()} replace />;
  if (!slug) return <Navigate to={ROUTES.state(state.slug)} replace />;
  if (state.slug !== params.stateSlug || slug !== params.citySlug) {
    return <Navigate to={ROUTES.city(state.slug, slug)} replace />;
  }

  return <PublicSeoPage model={model} loading={inventoryLoading || directoryLoading} />;
}

export default CityLandingPage;
